// Libs
import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Actions
import {
	setSelectedItems,
	reloadDatasource,
	setDataFilterLastRowId,
} from 'core/actions';
// Selectors
import { selectDisplaySort } from 'core/selectors';
// Utils
import FEED_KEYWORDS from 'core/keywords';
import TableUtils from './screens/StandardTableScreen/utils';
// Config
import ScreenBuilderConfig from './screen.config';
import { PAYMENT_TABLE_CONTEXTS } from './screens/StandardTableScreen/paymentPaycutFields';
// Illustrations
import {
	BuildingBlockIllustration,
	EmptyIllustration,
	PreferencesPopupIllustration,
} from 'components/Illustrations';
import { applyTransactionPresentationToDatasource, debounce } from 'utils/libs';

const ScreenBuilder = ({
	elementKey,
	screenKey,
	isMobile,
	selectedSubModuleId,
	isFetchingDisplay,
	isDatasourcePipelineLoading,
	dataFilterLastRowId,
	datasourceStatus,
	datasourcePaging,
	datasourceFields,
	datasource,
	datasourceSingleData,
	profile,
	onVisibleDatasourceChange,
}) => {
	const [building, setBuilding] = React.useState(true);
	const [builded, setBuilded] = React.useState(false);

	const dispatch = useDispatch();
	const sort = useSelector(selectDisplaySort);
	const presentedDatasource = React.useMemo(
		() => applyTransactionPresentationToDatasource(datasource),
		[datasource],
	);

	const sortedDatasource = React.useMemo(
		() =>
			TableUtils.sort({
				data: presentedDatasource,
				fields: datasourceFields,
				dataKey: sort.dataKey,
				dateFormat: profile?.user?.settings?.date_format,
				reverse: sort.direction === 'desc',
			}),
		[sort.dataKey, sort.direction, presentedDatasource, datasourceFields],
	);

	// Get Screen React Node
	const Screen = ScreenBuilderConfig.getAuthorizedScreenReactNode(screenKey);
	const isRootTableScreen =
		screenKey === ScreenBuilderConfig.authorizedScreenKeys().standardTableV1;
	const isLoading =
		isFetchingDisplay ||
		isDatasourcePipelineLoading ||
		datasourceStatus === FEED_KEYWORDS.STATUS.LOADING;
	const firstDataRequest = dataFilterLastRowId === 0;

	const debounced = React.useCallback(
		debounce(callback => callback(), 700),
		[],
	);

	// Handle animation interactions
	React.useEffect(() => {
		setBuilding(true);
		setBuilded(false);
	}, [selectedSubModuleId]);

	React.useEffect(() => {
		if (isLoading && firstDataRequest) {
			setBuilding(true);
			setBuilded(false);
		}
	}, [isLoading, firstDataRequest]);

	React.useEffect(() => {
		let isMounted = true;
		let t1, t2;
		t1 = setTimeout(
			() =>
				debounced(() => {
					if (Screen && !isLoading) {
						if (isMounted) setBuilding(false);
						t2 = setTimeout(() => isMounted && setBuilded(true), 300);
					}
				}),
			300,
		);
		return () => {
			isMounted = false;
			clearTimeout(t1);
			clearTimeout(t2);
		};
	}, [isLoading, selectedSubModuleId]);

	const buildingIllustration = (
		<BuildingBlockIllustration
			className={`animated ${building ? 'zoomIn' : 'zoomOut'}`}
			title='Construyendo el Feed... Un momento'
			width={isMobile ? '200px' : '400px'}
			fontSize='2em'
			loading={true}
		/>
	);

	// A missing screen is terminal only after its loading pipeline settles.
	if (!Screen) {
		if (isLoading) return buildingIllustration;
		return (
			<PreferencesPopupIllustration
				className={`animated ${building ? 'zoomIn' : 'zoomOut'}`}
				title='Esta consulta aún no tiene una pantalla configurada.'
				width={isMobile ? '200px' : '400px'}
				fontSize='2em'
			/>
		);
	}
	if (!builded) return buildingIllustration;

	// No data getted
	const emptyDatasource = datasource.length === 0;
	const datasourceLoadedSuccessfully =
		datasourceStatus === FEED_KEYWORDS.STATUS.SUCCESS;
	if (!isLoading && datasourceLoadedSuccessfully && emptyDatasource)
		return (
			<EmptyIllustration
				title='No se encontraron registros con el filtro actual'
				width={!isMobile ? '400px' : '200px'}
				fontSize='2em'
			/>
		);

	return (
		<Suspense fallback={null}>
			<Screen
				elementKey={elementKey}
				screenKey={screenKey}
				isMobile={isMobile}
				isLoading={isLoading}
				dataFilterLastRowId={dataFilterLastRowId}
				datasourcePaging={datasourcePaging}
				datasourceFields={datasourceFields}
				datasource={sortedDatasource}
				unsortedDatasource={presentedDatasource}
				datasourceSingleData={datasourceSingleData}
				profile={profile}
				tableContext={
					isRootTableScreen
						? PAYMENT_TABLE_CONTEXTS.ROOT_STANDARD_TABLE
						: undefined
				}
				setLastRowId={lastRowId => dispatch(setDataFilterLastRowId(lastRowId))}
				reloadDatasource={() => dispatch(reloadDatasource())}
				setSelectedItems={items => dispatch(setSelectedItems(items))}
				onVisibleDatasourceChange={onVisibleDatasourceChange}
			/>
		</Suspense>
	);
};

export default ScreenBuilder;
